import {UIGameManager} from '../../../../shared/scripts/ui_game/UIGameManager'
import {UIGamePlayerInformationView, UIPlayerAdditionalInformationLayer} from '../../../../shared/scripts/ui_game/ui_game_players/UIGamePlayerInformationView'
import {ItemCountView} from '../../../../shared/scripts/ui_canvas_engine/ItemCountView'
import {GameColor} from '../../../../shared/scripts/GameColor'
import {UIGameMechanic} from '../../../../shared/scripts/ui_game/UIGameEventController'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {UIGameSpriteHandler} from '../../../../shared/scripts/UIGameSpriteHandler'
import {PIXI} from '@colonist/pixi'

interface IconPositionalValues {
    iconW: number
    iconH: number
    iconX: number
    iconY: number
    countX: number
    countY: number
    countW: number
    countH: number
}
export class UIMGamePlayerInformationPlacementsView implements UIGameMechanic {
    private readonly uiGameManager: UIGameManager
    private readonly playerInformationView: UIGamePlayerInformationView
    private readonly shouldShowCityWall: boolean
    private readonly container: PIXI.Container

    private marginTop: number
    private marginLeft: number
    private maxIconWidth: number
    private maxIconHeight: number
    private maxCountWidth: number
    private maxCountHeight: number
    private textColor: string
    private spaceBetweenIconsAndCount: number
    private spaceBetweenIcons: number

    private roadView: ItemCountView
    private settlementView: ItemCountView
    private cityView: ItemCountView
    private cityWallView: ItemCountView | undefined

    constructor(uiGameManager: UIGameManager, playerInformationView: UIGamePlayerInformationView) {
        this.uiGameManager = uiGameManager
        this.playerInformationView = playerInformationView
        this.shouldShowCityWall = this.playerInformationView.cityWallPlayerState !== undefined
        this.container = this.playerInformationView.getContainerForAdditionalPlayerInformation(UIPlayerAdditionalInformationLayer.Layer1)

        this.createPlacementView()
        this.updatePlacementCountView()
        uiGameManager.eventController.registerUIGameMechanic(this)
    }

    private defineMaxIconAndCountDimensions(isCountHorizontal: boolean) {
        const container = this.container
        const margin = this.uiGameManager.canvasController.margin

        this.marginTop = container.height * 0.15

        const iconH = isCountHorizontal ? container.height * 0.4 : container.height * 0.45
        const iconW = iconH * 0.9
        const countH = isCountHorizontal ? iconH * 0.6 : iconW * 0.6
        const countW = iconW
        this.maxIconWidth = iconW
        this.maxIconHeight = iconH
        this.maxCountWidth = countW
        this.maxCountHeight = countH

        if(isCountHorizontal) {
            this.spaceBetweenIconsAndCount = margin * 2
            this.spaceBetweenIcons = margin * 2
            this.marginLeft = (container.width - (this.maxIconWidth * 2) - this.spaceBetweenIcons - (2 * this.maxCountWidth)) / 2
        } else {
            this.spaceBetweenIconsAndCount = iconH * 0.25
            this.spaceBetweenIcons = margin * 2
            this.marginLeft = (container.width - this.maxIconWidth * 3 - this.spaceBetweenIcons * 2) / 2
        }

        this.textColor = GameColor.getColor(GameColor.Color.GrayScale25)
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case GameStateEnums.UIGameEventEnums.PlayerControllerStateUpdated: this.updatePlacementCountView();break
            default: break
        }
    }

    private createPlacementView(): void {
        const shouldShowCountHorizontally = this.shouldShowCityWall
        this.defineMaxIconAndCountDimensions(shouldShowCountHorizontally)

        this.createRoadView(shouldShowCountHorizontally)
        this.createSettlementView(shouldShowCountHorizontally)
        this.createCityView(shouldShowCountHorizontally)
        if(this.shouldShowCityWall) this.createCityWallView()
    }

    private createRoadView(shouldShowCountHorizontally: boolean) {
        const texture = UIGameSpriteHandler.getRoadForPlayer(this.playerInformationView.player.color)
        const container = this.container

        const {iconW, iconH, iconX, iconY, countX, countY, countW, countH} = shouldShowCountHorizontally ? this.getPositionalValuesForHorizontalPlacement(texture, 0) : this.getPositionalValuesForVerticalPlacement(texture, 0)

        const iconPadding = 0

        const countYAdjustedForRoad = shouldShowCountHorizontally ? countY : countY + 1
        this.roadView = new ItemCountView(iconX, iconY, iconW, iconH, container, texture,
            countX, countYAdjustedForRoad, countW, countH, iconPadding, undefined, this.textColor)
        this.roadView.resetTextYPosition()
        this.roadView.count.changeStyle({fontSize: this.maxCountHeight})
    }

    private createSettlementView(shouldShowCountHorizontally: boolean) {
        const texture = UIGameSpriteHandler.getSettlementForPlayer(this.playerInformationView.player.color)
        const container = this.container

        const {iconW, iconH, iconX, iconY, countX, countY, countW, countH} = shouldShowCountHorizontally ? this.getPositionalValuesForHorizontalPlacement(texture, 1) : this.getPositionalValuesForVerticalPlacement(texture, 1)

        const iconPadding = 0

        this.settlementView = new ItemCountView(iconX, iconY, iconW, iconH, container, texture,
            countX, countY, countW, countH, iconPadding, undefined, this.textColor)
        this.settlementView.resetTextYPosition()
        this.settlementView.count.changeStyle({fontSize: this.maxCountHeight})
    }

    private createCityView(shouldShowCountHorizontally: boolean) {
        const texture = UIGameSpriteHandler.getCityForPlayer(this.playerInformationView.player.color)
        const container = this.container

        const {iconW, iconH, iconX, iconY, countX, countY, countW, countH} = shouldShowCountHorizontally ? this.getPositionalValuesForHorizontalPlacement(texture, 2) : this.getPositionalValuesForVerticalPlacement(texture, 2)

        const iconPadding = 0

        this.cityView = new ItemCountView(iconX, iconY, iconW, iconH, container, texture,
            countX, countY, countW, countH, iconPadding, undefined, this.textColor)
        this.cityView.resetTextYPosition()
        this.cityView.count.changeStyle({fontSize: this.maxCountHeight})
    }

    private createCityWallView() {
        const texture = UIGameSpriteHandler.getCityWallForPlayer(this.playerInformationView.player.color)
        const container = this.container

        const {iconW, iconH, iconX, iconY, countX, countY, countW, countH} = this.getPositionalValuesForHorizontalPlacement(texture, 3)

        this.cityWallView = new ItemCountView(iconX, iconY, iconW, iconH, container, texture,
            countX, countY, countW, countH, 0, undefined, this.textColor)
        this.cityWallView.resetTextYPosition()
        this.cityWallView.count.changeStyle({fontSize: this.maxCountHeight})
    }

    private updatePlacementCountView() {
        const player = this.playerInformationView.player
        if(this.playerInformationView.roadPlayerState) {
            this.roadView.updateText({key: 'strings:colonistCommon.utils.#buffer', options: {value: this.playerInformationView.roadPlayerState.bankRoadAmount}})
        }
        if(this.playerInformationView.cityPlayerState) {
            this.cityView.updateText({key: 'strings:colonistCommon.utils.#buffer', options: {value: this.playerInformationView.cityPlayerState.bankCityAmount}})
        }
        if(this.playerInformationView.settlementPlayerState) {
            this.settlementView.updateText({key: 'strings:colonistCommon.utils.#buffer', options: {value: this.playerInformationView.settlementPlayerState.bankSettlementAmount}})
        }
        if(this.shouldShowCityWall && this.playerInformationView.cityWallPlayerState) {
            this.cityWallView?.updateText({key: 'strings:colonistCommon.utils.#buffer', options: {value: this.playerInformationView.cityWallPlayerState.bankCityWallAmount}})
        }
    }

    private getPositionalValuesForVerticalPlacement(texture: PIXI.Texture, index: number): IconPositionalValues {
        let iconH = this.maxIconHeight
        let iconW = iconH * texture.width / texture.height
        if(iconW > this.maxIconWidth) {
            iconW = this.maxIconWidth
            iconH = iconW * texture.height / texture.width
        }

        const marginAdjustedForVisualDifference = index == 1 ? this.maxIconWidth * 0.08 : 0
        const iconX = this.marginLeft + ((2 * index + 1) * this.maxIconWidth - iconW) / 2 - marginAdjustedForVisualDifference + this.spaceBetweenIcons * index
        const iconY = this.marginTop + (this.maxIconHeight - iconH) / 2

        const countX = iconX + (iconW - this.maxIconWidth) / 2
        const countY = iconY + this.maxIconHeight + this.spaceBetweenIconsAndCount
        const countW = this.maxCountWidth
        const countH = this.maxCountHeight

        return {iconW: iconW, iconH: iconH, iconX: iconX, iconY: iconY, countX: countX, countY: countY, countW: countW, countH: countH}
    }

    private getPositionalValuesForHorizontalPlacement(texture: PIXI.Texture, index: number): IconPositionalValues {
        let iconH = this.maxIconHeight
        let iconW = iconH * texture.width / texture.height
        if(iconW > this.maxIconWidth) {
            iconW = this.maxIconWidth
            iconH = iconW * texture.height / texture.width
        }

        const marginAdjustedForVisualDifference = index == 0 ? this.maxIconWidth * 0.08 : 0
        const iconX = this.marginLeft + (index > 1 ? (this.maxIconWidth + this.maxCountWidth + + this.spaceBetweenIcons * 2) : 0)
        const iconY = this.marginTop + (index % 2 != 0 ? (this.maxIconHeight + this.maxCountWidth * 0.1) : 0)

        const countX = iconX + this.maxIconWidth * 0.8 + this.spaceBetweenIconsAndCount - marginAdjustedForVisualDifference
        const countY = iconY + (this.maxIconHeight - this.maxCountHeight) / 1.5
        const countW = this.maxCountWidth
        const countH = this.maxCountHeight

        return {iconW: iconW, iconH: iconH, iconX: iconX, iconY: iconY, countX: countX, countY: countY, countW: countW, countH: countH}
    }
}
